import { FC, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';

import API from 'core/Api';
import { createErrorFromResponse } from 'core/Error';

import { useModal } from 'store/modal';
import { useActiveClientContext } from 'store/active-client';

import { formatDate } from 'utils/helpers/formatters';
import { getUserInfo } from 'utils/auth';
import setServerErrors from 'utils/helpers/formErrors';
import { buildClientEntity, buildClientUrl } from 'utils/helpers/client';

import CreateEntity from 'components/crud/CreateEntity';
import { IFileDetails } from 'components/form/types';
import handleEmployeeCode from 'utils/helpers/handleEmployeeCode';
import { HandleEmployeeSignOptions } from 'components/form/CodeForm';
import LeaveForm from '../../components/LeaveForm';

interface ISelectOptionProps {
  label: string;
  value: string | number | boolean;
}
interface IDropdowns {
  types: ISelectOptionProps[];
  paid_sub_types: ISelectOptionProps[];
  additional_sub_types?: ISelectOptionProps[];
  substitute_employees?: ISelectOptionProps[];
}

const CreateForm: FC<{ dropdowns: IDropdowns }> = ({ dropdowns }) => {
  const { t } = useTranslation();
  const { client } = useActiveClientContext();
  const { showModal } = useModal();
  const history = useHistory();
  const [uploadedFiles, setUploadedFiles] = useState<IFileDetails[]>([]);

  const methods = useForm({
    shouldFocusError: false,
  });

  // Sign document as employee
  const handleEmployeeLeaveSign = (options: HandleEmployeeSignOptions) => {
    // Sign process used in "employee" mode.
    // AdES (Advanced Electronic Signature)
    //
    // The process takes place with one request to BE,
    // as the signing is done with self-signed certificates,
    // which are kept on the backend.
    const { docId, data, methodsProp } = options;
    return API.post(`documents/${docId}/sign-ades`, data)
      .then(res => {
        showModal({
          type: 'success',
          content: <>{t('leaves.createSuccessMsg')}.</>,
        });
        getUserInfo();
        return history.push(buildClientUrl('/my-leaves', client));
      })
      .catch(response => {
        const error = createErrorFromResponse(response);
        if (methodsProp) {
          setServerErrors(error.errors, methodsProp.setError);
        } else {
          showModal({
            type: 'error',
            content: createErrorFromResponse(error).message,
          });
        }
      });
  };
  return (
    <CreateEntity
      entity="leaves/request"
      methods={methods}
      onSuccess={(res: any) => {
        const { request_document_id: id } = res.data;
        return handleEmployeeCode({
          docId: id,
          showModal,
          heading: t('documents.table.signWithCodeLabel'),
          handleEmployeeSign: handleEmployeeLeaveSign,
        });
      }}
      queryKey={buildClientEntity('my-leaves', client)}
    >
      {(create: any) => (
        <LeaveForm
          title=""
          methods={methods}
          onFilesUploaded={(fl: IFileDetails[]) => {
            setUploadedFiles([...uploadedFiles, ...fl]);
          }}
          onFilesDeleted={fl => {
            setUploadedFiles(current => current.filter(file => fl.name !== file.name));
          }}
          handleSubmit={data => {
            // Check if the selected type is not one of the standard types
            const standardLeaveTypes = ['paid', 'unpaid', 'sick', 'additional'];
            const isNonStandardType = !standardLeaveTypes.includes(data.type.value);

            // Determine the type and subType based on the selection
            let type;
            let subType;
            if (isNonStandardType) {
              type = 'additional';
              subType = data.type.value;
            } else {
              type = data.type.value;
              // Only set subType for 'paid' or 'additional' types
              if (type === 'paid' || type === 'additional') {
                subType = data?.sub_type?.value ? data.sub_type.value : 'paid_ordinary';
              }
            }

            const formatData = {
              type: isNonStandardType ? 'additional' : data?.type.value,
              date_from: formatDate(data.date_from, 'YYY-mm-dd'),
              // if halfDay is checked, set dateTo to be the same day
              date_to: data?.half_day ? formatDate(data.date_from, 'YYY-mm-dd') : formatDate(data.date_to, 'YYY-mm-dd'),
              attachments: uploadedFiles.map(file => file.id),
              // if halfDay is NOT checked, set part_of_day to undefined
              half_day: data?.half_day,
              part_of_day: data?.half_day ? data?.part_of_day?.value : undefined,
              comment: data.comment || undefined,
              substitute_employee_id: data?.substitute_employee_id?.value || undefined,
              ...(subType ? { sub_type: subType } : {}),
            };

            return create(formatData).catch((response: any) => {
              const error = createErrorFromResponse(response);

              const conflictMessage = error.errors?.errors_common?.[0];
              const isOverlapError = conflictMessage?.match(
                /се припокрива с вече потвърден отпуск, номер \d+|chosen period overlaps with a confirmed leave, number \d+/i
              );

              if (isOverlapError) {
                return showModal({
                  type: 'confirm',
                  heading: t('leaves.interruptOverlapHeading'),
                  content: (
                    <>
                      {conflictMessage}
                      <br />
                      {t('leaves.interruptConfirmMsg')}
                    </>
                  ),
                  onConfirm: async () => {
                    showModal({
                      type: 'loading',
                      content: t('leaves.interruptLoadingMsg'),
                    });

                    return create({ ...formatData, interrupt: true });
                  },
                });
              }

              if (error.errors) {
                setServerErrors(error.errors, methods.setError);
              } else {
                showModal({
                  type: 'error',
                  content: error.message,
                });
              }
            });
          }}
          btnLabel={t('common.requestAndSign')}
          types={dropdowns.types}
          subTypes={dropdowns.paid_sub_types}
          additioanlSubTypes={dropdowns?.additional_sub_types}
          substituteEmployees={dropdowns?.substitute_employees}
        />
      )}
    </CreateEntity>
  );
};

export default CreateForm;
